import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { teacherAPI } from '../../services/api';
import './TeacherDashboard.css';

function TeacherDashboard({ user, onLogout }) {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(searchParams.get('group') || 'all');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await teacherAPI.getDashboard();
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, details) => {
    switch (status) {
      case 'NOT_AVAILABLE':
        return <span className="badge badge-secondary">Locked</span>;
      case 'AVAILABLE':
        return <span className="badge badge-info">Available</span>;
      case 'IN_PROGRESS':
        return <span className="badge badge-warning">{details.tasksCompleted}/{details.totalTasks} tasks</span>;
      case 'QUIZ_UNLOCKED':
        return <span className="badge badge-info">Quiz Available</span>;
      case 'PASSED':
        return <span className="badge badge-success">{details.score}% ✓</span>;
      case 'FAILED':
        return <span className="badge badge-danger">{details.score}%</span>;
      default:
        return <span className="badge badge-secondary">{status}</span>;
    }
  };

  const getExerciseShortName = (title) => {
    const match = title.match(/Exercise (\d+)/i);
    return match ? `Exercise ${match[1]}` : title;
  };

  const filteredStudents = data?.students.filter(student => {
    const matchesSearch = student.fullName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGroup = selectedGroup === 'all' || student.groupName === selectedGroup;
    return matchesSearch && matchesGroup;
  }) || [];

  const uniqueGroups = [...new Set(data?.students.map(s => s.groupName).filter(Boolean))] || [];
  const dashboardQuery = selectedGroup === 'all'
    ? ''
    : `?group=${encodeURIComponent(selectedGroup)}`;

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="loading">Loading dashboard...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="error-message">{error}</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="teacher-dashboard">
        <h1>Student Progress Dashboard</h1>

        {data.students.length === 0 ? (
          <div className="card">
            <p>No students yet. <Link to="/teacher/students">Add students</Link></p>
          </div>
        ) : (
          <>
            <div className="dashboard-filters">
              <input
                type="text"
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="group-filter"
              >
                <option value="all">All Groups</option>
                {uniqueGroups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </div>

            <div className="dashboard-table-container">
              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Group</th>
                    {data.exercises.map(ex => (
                      <th key={ex.id}>{getExerciseShortName(ex.title)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map(student => (
                    <tr key={student.id}>
                      <td>
                        <Link to={`/teacher/students/${student.id}${dashboardQuery}`} className="student-link">
                          {student.fullName}
                        </Link>
                      </td>
                      <td>{student.groupName || '-'}</td>
                      {student.exercises.map(ex => (
                        <td key={ex.exerciseId}>
                          {getStatusBadge(ex.status, ex)}
                          {ex.attempts > 0 && (
                            <div className="attempts-info">
                              {ex.attempts} {ex.attempts === 1 ? 'attempt' : 'attempts'}
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

export default TeacherDashboard;
